import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../utils/api';
import { getPropertyConfig } from '../utils/auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Camera, X, MapPin, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';

export default function QRScanner() {
  const [scanning, setScanning] = useState(false);
  const [itemData, setItemData] = useState(null);
  const [stockData, setStockData] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMode, setActionMode] = useState(null); // 'in' or 'out'
  const [qty, setQty] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const scannerRef = useRef(null);
  const propConfig = getPropertyConfig();
  const hasPO = propConfig?.has_po !== false;

  useEffect(() => {
    api.get('/warehouses').then(res => {
      setWarehouses(res.data);
      if (res.data.length > 0) setSelectedWarehouse(res.data[0].id);
    }).catch(() => {});
  }, []);

  const startScanner = async () => {
    try {
      const html5QrCode = new Html5Qrcode("qr-reader");
      scannerRef.current = html5QrCode;
      setScanning(true);
      setItemData(null);
      setActionMode(null);

      await html5QrCode.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          html5QrCode.stop().catch(() => {});
          setScanning(false);
          handleScanResult(decodedText);
        },
        () => {}
      );
    } catch {
      toast.error('Tidak bisa akses kamera. Pastikan izin kamera sudah diberikan.');
      setScanning(false);
    }
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) scannerRef.current.stop().catch(() => {});
      } catch {}
      setScanning(false);
    }
  };

  const handleScanResult = async (text) => {
    setLoading(true);
    try {
      const itemId = text.trim();
      const [itemRes, stockRes] = await Promise.all([
        api.get('/items'),
        api.get('/stock')
      ]);
      const item = itemRes.data.find(i => i.id === itemId);
      if (!item) {
        toast.error('Item tidak ditemukan');
        setLoading(false);
        return;
      }
      setItemData(item);
      setStockData(stockRes.data.filter(s => s.item_id === itemId));
      toast.success(`Item: ${item.name}`);
    } catch {
      toast.error('Gagal mengambil data item');
    }
    setLoading(false);
  };

  const handleQuickAction = async () => {
    if (!qty || parseFloat(qty) <= 0) {
      toast.error('Masukkan quantity');
      return;
    }
    if (!selectedWarehouse) {
      toast.error('Pilih gudang');
      return;
    }
    setSubmitting(true);
    try {
      const today = new Date().toISOString();
      const itemPayload = {
        item_id: itemData.id,
        item_name: itemData.name,
        unit: itemData.unit,
        quantity: parseFloat(qty),
        amount: 0
      };

      if (actionMode === 'in') {
        await api.post('/receiving', {
          document: `QR-IN-${Date.now()}`,
          date: today,
          location: selectedWarehouse,
          items: [itemPayload]
        });
        // Auto-release
        const recvRes = await api.get('/receiving');
        const latest = recvRes.data[0];
        if (latest && latest.status === 'Open') {
          await api.patch(`/receiving/${latest.receiving_number}/release`);
        }
        toast.success(`+${qty} ${itemData.unit} ${itemData.name} masuk ke ${selectedWarehouse}`);
      } else {
        await api.post('/store-requisitions', {
          document: `QR-OUT-${Date.now()}`,
          date: today,
          location: selectedWarehouse,
          items: [{
            item_id: itemData.id,
            item_name: itemData.name,
            unit: itemData.unit,
            quantity: parseFloat(qty),
            warehouse: selectedWarehouse
          }]
        });
        const srRes = await api.get('/store-requisitions');
        const latest = srRes.data[0];
        if (latest && latest.status === 'Open') {
          await api.patch(`/store-requisitions/${latest.sr_number}/release`);
        }
        toast.success(`-${qty} ${itemData.unit} ${itemData.name} keluar dari ${selectedWarehouse}`);
      }

      // Refresh stock
      const stockRes = await api.get('/stock');
      setStockData(stockRes.data.filter(s => s.item_id === itemData.id));
      setActionMode(null);
      setQty('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan');
    }
    setSubmitting(false);
  };

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        try {
          const state = scannerRef.current.getState();
          if (state === 2) scannerRef.current.stop().catch(() => {});
        } catch {}
      }
    };
  }, []);

  const totalStock = stockData.reduce((sum, s) => sum + s.quantity, 0);

  return (
    <div className="space-y-6 fade-in" data-testid="qr-scanner-page">
      <div>
        <h1 className="text-4xl font-bold text-primary mb-2">QR Scanner</h1>
        <p className="text-muted-foreground">Scan QR → lihat stok → langsung tambah/kurangi</p>
      </div>

      <Card className="p-6">
        <div className="flex flex-col items-center">
          {!scanning && !itemData && (
            <div className="text-center space-y-4">
              <div className="w-24 h-24 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                <Camera className="w-12 h-12 text-primary" />
              </div>
              <p className="text-muted-foreground">Arahkan kamera ke QR code pada barang</p>
              <Button onClick={startScanner} className="btn-primary" data-testid="start-scan-btn">
                <Camera className="w-4 h-4 mr-2" /> Mulai Scan
              </Button>
            </div>
          )}

          <div id="qr-reader" className={`w-full max-w-md ${scanning ? '' : 'hidden'}`} />

          {scanning && (
            <Button onClick={stopScanner} variant="destructive" className="mt-4" data-testid="stop-scan-btn">
              <X className="w-4 h-4 mr-2" /> Stop
            </Button>
          )}
        </div>
      </Card>

      {loading && <Card className="p-6 text-center"><p className="text-muted-foreground">Mengambil data...</p></Card>}

      {itemData && !loading && (
        <Card className="p-6 space-y-4" data-testid="scan-result">
          {/* Item info */}
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-primary">{itemData.name}</h2>
              <p className="text-muted-foreground">{itemData.category} &middot; {itemData.unit}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-primary">{totalStock}</p>
              <p className="text-sm text-muted-foreground">Total ({itemData.unit})</p>
            </div>
          </div>

          {/* Stock per warehouse */}
          {stockData.length > 0 && (
            <div className="grid gap-2">
              {stockData.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{s.warehouse}</span>
                  </div>
                  <span className="font-bold text-primary">{s.quantity} {s.unit}</span>
                </div>
              ))}
            </div>
          )}

          {/* Quick action buttons */}
          {!actionMode && (
            <div className="flex gap-3 pt-2">
              <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => setActionMode('in')} data-testid="quick-in-btn">
                <Plus className="w-4 h-4 mr-2" /> {hasPO ? 'Stock In' : 'Barang Masuk'}
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => setActionMode('out')} data-testid="quick-out-btn">
                <Minus className="w-4 h-4 mr-2" /> {hasPO ? 'Stock Out' : 'Barang Keluar'}
              </Button>
              <Button variant="outline" onClick={() => { setItemData(null); startScanner(); }} data-testid="scan-again-btn">
                <Camera className="w-4 h-4 mr-2" /> Scan Lagi
              </Button>
            </div>
          )}

          {/* Quick form */}
          {actionMode && (
            <div className="border-t pt-4 space-y-3" data-testid="quick-action-form">
              <h3 className="font-semibold text-lg">
                {actionMode === 'in' ? (hasPO ? 'Stock In' : 'Barang Masuk') : (hasPO ? 'Stock Out' : 'Barang Keluar')}
                : {itemData.name}
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Gudang</Label>
                  <select
                    value={selectedWarehouse}
                    onChange={(e) => setSelectedWarehouse(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="quick-warehouse-select"
                  >
                    {warehouses.map(w => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Quantity ({itemData.unit})</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    placeholder="Jumlah"
                    autoFocus
                    data-testid="quick-qty-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  className={`flex-1 ${actionMode === 'in' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
                  onClick={handleQuickAction}
                  disabled={submitting}
                  data-testid="quick-save-btn"
                >
                  {submitting ? 'Menyimpan...' : 'Simpan'}
                </Button>
                <Button variant="outline" onClick={() => { setActionMode(null); setQty(''); }}>Batal</Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
